import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
    PiUserLight,
    PiEnvelopeLight,
    PiPhoneLight,
    PiBuildingsLight,
} from "react-icons/pi";
import { useTranslation } from "react-i18next";
import Dropdown from "../../../shared/TailwindComponents/Dropdown";
import Button from "../../../shared/TailwindComponents/Button";
import Input from "../../../shared/TailwindComponents/Input";
import { useAppDispatch } from "../../../../store";
import { hideModal, showModal } from "../../../../store/actions/modalActions";
import apis from "../../../../utils/apis";
import DateTimePicker from "../../../shared/TailwindComponents/DatePicker";
import { inviteExternalCollaborator, sendExternalCollaboratorInvite } from "../../../../store/actions/organization/ExternalPeopleActions";
import { fetchMatters } from "../../../../store/actions/Matters/MatterActions";

interface ExternalOnboardingProps {
    onClose: () => void;
}

const ExternalOnboarding: React.FC<ExternalOnboardingProps> = ({ onClose }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [step, setStep] = useState<"check" | "new">("check");
    const type = "Individual";
    const [checking, setChecking] = useState(false);
    const [cases, setCases] = useState<any>([])
    const allCases: any[] = []

    const ROLES = [
        { value: 'Attorney', label: 'Attorney' },
        { value: 'Litigator', label: 'Litigator' },
        { value: 'Paralegal', label: 'Paralegal' },
        { value: 'Arbitrator', label: 'Arbitrator' },
        { value: 'Mediator', label: 'Mediator' },
        { value: 'Conciliator', label: 'Conciliator' },
        { value: 'Fact-Finder', label: 'Fact-Finder' },
        { value: 'Ombudsman', label: 'Ombudsman' },
      ];


    useEffect(() => {
        if (allCases.length == 0) {
            dispatch(fetchMatters())
        }
    }, [allCases])

    useEffect(() => {
        if (allCases.length > 0) {
            setCases(
                allCases.map((item) => ({
                    value: item.CaseID,
                    label: item.CaseName,
                }))
            );
        }
    }, [allCases]);

    const formik = useFormik({
        initialValues: {
            Type: "Individual",
            Email: "",
            Matter: "",
            FirstName: "",
            LastName: "",
            PhoneNumber: "",
            Role: "",
            OrganizationName: "",
            OrganizationEmail: "",
            OrganizationAddress: "",
            OrganizationPhone: "",
            EndDate: "",
            Permission: 'read'
        },
        validationSchema: Yup.object({
            Type: Yup.string().required(),
            Email: Yup.string().email(t("Invalid email")).required(t("Email is required")),
            Matter: Yup.string().required(t("Please select a matter")),
            ...(step === "new" && {
                FirstName: Yup.string().when("Type", {
                    is: "Individual",
                    then: (schema) => schema.required(t("First name required")),
                }),
                LastName: Yup.string().when("Type", {
                    is: "Individual",
                    then: (schema) => schema.required(t("Last name required")),
                }),
                OrganizationName: Yup.string().when("Type", {
                    is: "Organization",
                    then: (schema) => schema.required(t("Organization name required")),
                }),
                OrganizationEmail: Yup.string().when("Type", {
                    is: "Organization",
                    then: (schema) =>
                        schema.email(t("Invalid email")).required(t("Organization email required")),
                }),
            }),
        }),
        onSubmit: async (values) => {
            if (step === "check") {
                try {
                    setChecking(true);
                    const res = await apis({
                        method: "POST",
                        url: "user/check_email",
                        data: { Email: values.Email },
                    });
                    
                    const data = res && res.data;

                    if (res && res.status == 200) {
                        dispatch(
                            showModal({
                                showModal: true,
                                type: "confirmInvitation",
                                message: t("Collaboration invitation"),
                                inputData: `${t("Your invitation is ready.")} (${data.name}, ${values.Email}). ${t(
                                    "Do you want to send them an invite to collaborate on this matter?"
                                )}`,
                                onConfirm: async () => {
                                    const resp = await dispatch(sendExternalCollaboratorInvite(values))
                                    if(resp){
                                        hideModal();
                                    }
                                },
                            })
                        );
                    } else {
                        setStep("new");
                    }
                } catch (err) {
                    console.error("Error checking external:", err);
                    setStep("new");
                } finally {
                    setChecking(false);
                }
            } else {
                const resp = await dispatch(inviteExternalCollaborator(values))
                if(resp){
                    onClose();

                }
            }
        },
    });

    return (
        <div className="px-6 pb-6 bg-white dark:bg-gray-900 rounded-xl shadow-lg max-w-4xl w-full mx-auto mt-5 flex flex-col overflow-y-auto max-h-[calc(100vh-50px)] custom-scrollbar-main">
            <div className="sticky top-0 z-10 pt-6 bg-white dark:bg-gray-900 pb-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                <div className="flex gap-2 items-center">
                    <PiUserLight size={28} className="text-secondary-900" />
                    <div className="block space-y-1">
                        <h2 className="text-xl text-gray-900 dark:text-white">
                            {t("Add external")}
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-gray-200">
                            {t("Invite an external collaborator or organization.")}
                        </p>
                    </div>
                </div>
                <Button variant="neutral" size="small" onClick={onClose}>
                    {t("Cancel")}
                </Button>
            </div>

            <form
                id="onboard-external-form"
                onSubmit={formik.handleSubmit}
                className="space-y-6 mt-4 flex-1"
            >
                <div className="grid grid-cols-2 gap-6">
                    <Input
                        label={t("Email")}
                        name="Email"
                        type="email"
                        value={formik.values.Email}
                        onChange={formik.handleChange}
                        error={formik.touched.Email ? formik.errors.Email : undefined}
                        placeholder={t("example@mail.com")}
                        disabled={step == "new"}
                        icon={<PiEnvelopeLight size={20} className="text-gray-500" />}
                    />
                    <Dropdown
                        label={t("Select matter")}
                        options={cases || []}
                        value={formik.values.Matter}
                        onChange={(val) => formik.setFieldValue("Matter", val)}
                        placeholder={t("Select matter/case")}
                        infoText="Select matter on which this collaborator will work"
                    />

                    <Dropdown
                        label={t("Access permission")}
                        options={[
                            { value: "read", label: t("Read") },
                            { value: "write", label: t("Write") },
                        ]}
                        value={formik.values.Matter}
                        onChange={(val) => formik.setFieldValue("Permission", val)}
                        placeholder={t("Select permission")}
                        infoText={t("What access level a collaborator has on the selected matter.")}
                    />

                    <Dropdown
                        label={t("Select role")}
                        options={ROLES}
                        value={formik.values.Matter}
                        onChange={(val) => formik.setFieldValue("Role", val)}
                        placeholder={t("Select role")}
                        infoText={t("What role a collaborator has on the selected matter.")}
                    />
                </div>

                {step === "new" && (
                    <div className="grid grid-cols-2 gap-6">
                        {type === "Individual" && (
                            <>
                                <Input
                                    label={t("First name")}
                                    name="FirstName"
                                    type="text"
                                    value={formik.values.FirstName}
                                    onChange={formik.handleChange}
                                    error={
                                        formik.touched.FirstName ? formik.errors.FirstName : undefined
                                    }
                                    placeholder={t("John")}
                                    icon={<PiUserLight size={20} className="text-gray-500" />}
                                />
                                <Input
                                    label={t("Last name")}
                                    name="LastName"
                                    type="text"
                                    value={formik.values.LastName}
                                    onChange={formik.handleChange}
                                    error={
                                        formik.touched.LastName ? formik.errors.LastName : undefined
                                    }
                                    placeholder={t("Doe")}
                                    icon={<PiUserLight size={20} className="text-gray-500" />}
                                />
                                <Input
                                    label={t("Phone number")}
                                    name="PhoneNumber"
                                    type="text"
                                    value={formik.values.PhoneNumber}
                                    onChange={formik.handleChange}
                                    placeholder={t("#####")}
                                    icon={<PiPhoneLight size={20} className="text-gray-500" />}
                                />
                                <Input
                                    label={t("Linked organization (optional)")}
                                    name="OrganizationName"
                                    type="text"
                                    value={formik.values.OrganizationName}
                                    onChange={formik.handleChange}
                                    placeholder={t("Doe Law Firm")}
                                    icon={<PiBuildingsLight size={20} className="text-gray-500" />}
                                    infoText={t("If the collaborator works at a different firm, add the firm name here.")}
                                    infoTrigger="hover"  // or "click"
                                />

                                <DateTimePicker
                                    id="task-deadline"
                                    label={t("Task deadline")}
                                    selected={formik.values.EndDate ? new Date(formik.values.EndDate) : null}
                                    onChange={(date) =>
                                        formik.setFieldValue(
                                            "EndDate",
                                            date ? date.toISOString() : ""
                                        )
                                    }
                                    placeholder={t("Select due date")}
                                    required
                                    error={formik.touched.EndDate && Boolean(formik.errors.EndDate)}
                                    helperText={formik.touched.EndDate ? (formik.errors.EndDate as string) : ""}
                                />


                            </>
                        )}
                    </div>
                )}

                <div className="flex justify-end">
                    <Button
                        variant="primary"
                        size="small"
                        type="submit"
                        form="onboard-external-form"
                        className="relative"
                        disabled={checking}
                    >
                        {checking
                            ? t("Checking...")
                            : step === "check"
                                ? t("Invite")
                                : t("Save & invite")}
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default ExternalOnboarding;
